import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Typography, useTheme } from "@mui/material";
import { AssessmentLeaderboardItem } from "../../../axios/quiz";
import { useAuthStore } from "../../../stores/authStore";
import useUserStore from "../../../stores/userStore";
import {
  getAssessmentUserPlace,
  sortAssessmentLeaderboard,
} from "../assessment-contest-utils";
import {
  formatAssessmentBonusSuffix,
  formatAssessmentScoreNumber,
} from "../../../utils/assessmentScore";
import {
  actionButtonStyle,
  actionWrapStyle,
  cardStyle,
  currentUserBadgeStyle,
  emptyStyle,
  headerStyle,
  liveChipStyle,
  medalStyle,
  medalWrapStyle,
  nameStyle,
  placeBadgeStyle,
  pointsBonusStyle,
  pointsStyle,
  listContainerStyle,
  rowLeftStyle,
  rowStyle,
  titleStyle,
} from "./styles";

interface AssessmentLeaderboardCardProps {
  items?: AssessmentLeaderboardItem[];
  currentUserPlace?: number | null;
  title?: string;
  emptyMessage?: string;
  maxItems?: number;
  compact?: boolean;
  showExpandAction?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const AssessmentLeaderboardCard = ({
  items = [],
  currentUserPlace,
  title = "Leaderboard",
  emptyMessage = "Leaderboard will appear here when challenge data is available.",
  maxItems,
  compact = false,
  showExpandAction = false,
  onExpandedChange,
}: AssessmentLeaderboardCardProps) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);
  const authUserId = useAuthStore((state) => state.userId);
  const storedUserId = useUserStore((state) => state.user.id);
  const currentUserId = storedUserId || authUserId || null;

  const resolvedCurrentUserPlace = getAssessmentUserPlace(
    items,
    currentUserId,
    currentUserPlace ?? null
  );

  const rankedItems = useMemo(() => {
    let previousRankingKey: string | null = null;
    let previousRank = 0;

    return sortAssessmentLeaderboard(items, currentUserId).map((item, index) => {
      const itemRankingKey = `${item.user_score ?? item.score}:${item.mandatory_score ?? 0}`;
      const rank =
        previousRankingKey === itemRankingKey ? previousRank : index + 1;
      previousRankingKey = itemRankingKey;
      previousRank = rank;

      return {
        ...item,
        rank,
      };
    });
  }, [items, currentUserId]);

  const canExpand =
    showExpandAction &&
    typeof maxItems === "number" &&
    rankedItems.length > maxItems;
  const topLabelCount = typeof maxItems === "number" ? maxItems : 3;

  const visibleItems = canExpand
    ? isExpanded
      ? rankedItems
      : rankedItems.slice(0, maxItems)
    : rankedItems.slice(0, maxItems ?? rankedItems.length);

  const listContent =
    visibleItems.length > 0 ? (
      visibleItems.map((item, index) => {
          const isCurrentUser = currentUserId
            ? item.user_id === currentUserId
            : resolvedCurrentUserPlace !== null &&
              resolvedCurrentUserPlace !== undefined &&
              resolvedCurrentUserPlace === item.rank;
          const place =
            isCurrentUser &&
            resolvedCurrentUserPlace !== null &&
            resolvedCurrentUserPlace !== undefined
              ? resolvedCurrentUserPlace
              : item.rank;
          const displayName = isCurrentUser ? "You" : item.name;
          const showCompactCurrentUserBadge = compact && isCurrentUser;
          const showMedalBadge = place <= 3 && !showCompactCurrentUserBadge;
          const showDivider = index < visibleItems.length - 1;
          const userScore =
            typeof item.user_score === "number" ? item.user_score : item.score;
          const gamificationBonus =
            typeof item.gamification_bonus_score === "number"
              ? item.gamification_bonus_score
              : typeof item.bonus_score === "number"
                ? item.bonus_score
                : 0;
          const bonusLabel = formatAssessmentBonusSuffix(
            gamificationBonus,
          ).trim();

          return (
            <Box
              key={item.user_id}
              sx={rowStyle(isCurrentUser, compact, showDivider, theme)}
            >
              <Box sx={rowLeftStyle}>
                {showCompactCurrentUserBadge ? (
                  <Box sx={currentUserBadgeStyle(compact)}>{place}</Box>
                ) : showMedalBadge ? (
                  <Box sx={medalWrapStyle(compact)}>
                    <Box sx={medalStyle(place, compact)}>{place}</Box>
                  </Box>
                ) : (
                  <Box sx={placeBadgeStyle(place, compact, theme)}>{place}</Box>
                )}
                <Typography
                  noWrap
                  title={displayName}
                  sx={nameStyle(theme, compact)}
                >
                  {displayName}
                </Typography>
              </Box>
              <Typography
                component="span"
                sx={{
                  ...pointsStyle(compact, theme),
                  display: "inline-flex",
                  alignItems: "baseline",
                  whiteSpace: "nowrap",
                }}
              >
                {`${formatAssessmentScoreNumber(userScore)} pts`}
                {bonusLabel ? (
                  <Box component="span" sx={pointsBonusStyle(compact)}>
                    {bonusLabel}
                  </Box>
                ) : null}
              </Typography>
            </Box>
          );
        })
    ) : (
      <Typography sx={emptyStyle(theme)}>{emptyMessage}</Typography>
    );

  return (
    <Box sx={cardStyle(theme, compact, isExpanded)}>
      <Box sx={headerStyle}>
        <Typography sx={titleStyle(compact, theme)}>{title}</Typography>
        <Chip size="small" label="Live" sx={liveChipStyle} />
      </Box>
      <Box sx={listContainerStyle(isExpanded, compact)}>{listContent}</Box>
      {canExpand ? (
        <Box sx={actionWrapStyle}>
          <Button
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={actionButtonStyle(compact)}
          >
            {isExpanded
              ? `Show top ${topLabelCount} ->`
              : "View full leaderboard ->"}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default AssessmentLeaderboardCard;
